import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import NewsService from "../services/newsService";
import { db } from "../firebase";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  where,
} from "firebase/firestore";

const toTitleTR = (s = "") =>
  s
    .toLocaleLowerCase("tr")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map((w) => (w ? w[0].toLocaleUpperCase("tr") + w.slice(1) : ""))
    .join(" ");

// Ana Kategoriler ve Haberler Bölümü
const CategoriesAndNewsSection = ({ setCurrentPage }) => {
  return (
    <div className="container mx-auto px-4 py-4 md:py-8">
      <div className="flex flex-col lg:flex-row gap-4 md:gap-6">
        {/* Sol - Kategoriler Listesi */}
        <CategoriesSidebar setCurrentPage={setCurrentPage} />

        {/* Sağ - Haberler ve Gold Üyeler */}
        <div className="flex-1 w-full lg:w-auto">
          <NewsSection />
          <GoldMembersSection />
        </div>
      </div>
    </div>
  );
};

// Sol Kategoriler Sidebar
const CategoriesSidebar = ({ setCurrentPage }) => {
  const [fbFirms, setFbFirms] = useState([]);
  const [openSector, setOpenSector] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const q = query(collection(db, "firmalar"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      setFbFirms(snap.docs.map((d) => d.data()));
    });
    return () => unsub();
  }, []);

  // Ana sektörler ve her birine ait alt sektörler
  const sectorMap = useMemo(() => {
    const map = new Map(); // anaSektör → { count, altSektorler: Map<altSektör, count> }
    fbFirms.forEach((f) => {
      const anaSektorler = f.secilenSektorler?.length ? f.secilenSektorler : [];
      const altSektorler = f.altSektorler || [];
      anaSektorler.forEach((ana) => {
        if (!ana?.trim()) return;
        if (!map.has(ana)) map.set(ana, { count: 0, altMap: new Map() });
        const entry = map.get(ana);
        entry.count++;
        altSektorler.forEach((alt) => {
          if (!alt?.trim()) return;
          entry.altMap.set(alt, (entry.altMap.get(alt) || 0) + 1);
        });
      });
    });
    return Array.from(map, ([name, { count, altMap }]) => ({
      name,
      count,
      altSektorler: Array.from(altMap, ([altName, altCount]) => ({
        name: altName,
        count: altCount,
      })).sort((a, b) => b.count - a.count),
    })).sort((a, b) => b.count - a.count);
  }, [fbFirms]);

  const handleAnaClick = (name) => {
    setOpenSector(openSector === name ? null : name);
  };

  const handleAltClick = (altSektör) => {
    sessionStorage.setItem("pref:altSektor", altSektör);
    setCurrentPage("firms");
  };

  return (
    <div className="w-full lg:w-80 bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="bg-slate-700 text-white p-3 md:p-4 rounded-t-lg flex items-center">
        <svg
          className="w-4 h-4 md:w-5 md:h-5 mr-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 6h16M4 12h16M4 18h16"
          />
        </svg>
        <h3 className="font-bold text-base md:text-lg">KATEGORİLER</h3>
      </div>

      <div className="max-h-[500px] overflow-y-auto">
        {sectorMap.map((sector) => (
          <div
            key={sector.name}
            className="border-b border-gray-100 last:border-0"
          >
            {/* Ana Sektör */}
            <button
              type="button"
              onClick={() => handleAnaClick(sector.name)}
              className="w-full flex items-center justify-between px-3 md:px-4 py-2.5 hover:bg-gray-50 transition text-left"
            >
              <div className="flex items-center gap-2">
                <svg
                  className="w-4 h-4 text-slate-500 shrink-0"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
                </svg>
                <span className="text-xs md:text-sm font-medium text-gray-700">
                  {toTitleTR(sector.name)}
                </span>
                <span className="text-xs text-gray-400">({sector.count})</span>
              </div>
              <svg
                className={`w-4 h-4 text-gray-400 transition-transform duration-200 shrink-0 ${
                  openSector === sector.name ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {/* Alt Sektörler */}
            {openSector === sector.name && (
              <div className="bg-slate-50 border-t border-gray-100">
                {/* Ana sektörün kendisine git */}
                <button
                  type="button"
                  onClick={() => {
                    sessionStorage.setItem("selectedCategory", sector.name);
                    setCurrentPage("firms");
                  }}
                  className="w-full text-left px-6 py-2 text-xs text-amber-700 font-semibold hover:bg-amber-50 transition flex items-center gap-2 border-b border-gray-100"
                >
                  <span>→</span> Tümünü gör ({sector.count})
                </button>

                {sector.altSektorler.length > 0 ? (
                  sector.altSektorler.map((alt) => (
                    <button
                      key={alt.name}
                      type="button"
                      onClick={() => handleAltClick(alt.name)}
                      className="w-full text-left px-6 py-2 text-xs text-gray-600 hover:bg-slate-100 hover:text-slate-800 transition flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0" />
                        {toTitleTR(alt.name)}
                      </div>
                      <span className="text-gray-400">({alt.count})</span>
                    </button>
                  ))
                ) : (
                  <p className="px-6 py-2 text-xs text-gray-400">
                    Alt sektör yok
                  </p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Alt Buton */}
      <div className="p-3 md:p-4 border-t border-gray-200">
        <button
          onClick={() => setCurrentPage("firms")}
          className="w-full bg-amber-400 hover:bg-amber-500 text-gray-800 py-2 md:py-3 rounded font-bold transition flex items-center justify-center text-sm md:text-base"
        >
          Tümünü Gör
          <svg
            className="w-3 h-3 md:w-4 md:h-4 ml-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
      </div>

      {/* Reklam Alanı */}
      <div className="mt-4 bg-gray-100 h-24 md:h-32 rounded flex items-center justify-center px-4">
        <button
          onClick={() => setCurrentPage("firmAdd")}
          className="bg-slate-700 hover:bg-slate-800 text-white px-4 md:px-8 py-2 md:py-3 rounded-lg font-semibold transition flex items-center text-xs md:text-base"
        >
          <svg
            className="w-4 h-4 md:w-5 md:h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
            />
          </svg>
          HEMEN FİRMANI EKLE
        </button>
      </div>
    </div>
  );
};
// Haber Detay Dialog
const NewsDialog = ({ news, isOpen, onClose }) => {
  const [imgIndex, setImgIndex] = React.useState(0);
  React.useEffect(() => {
    setImgIndex(0);
  }, [news?.id]);

  const allImages = [news?.imageUrl, ...(news?.extraImages || [])].filter(
    Boolean
  );

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen || !news) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-200 bg-slate-700 text-white">
          <h2 className="text-lg md:text-2xl font-bold pr-4">{news.title}</h2>
          <button
            onClick={onClose}
            className="flex-shrink-0 p-2 hover:bg-slate-600 rounded-full transition"
          >
            <svg
              className="w-5 h-5 md:w-6 md:h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 p-4 md:p-6">
          {/* Tarih */}
          <div className="flex items-center text-sm text-gray-600 mb-4">
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            {NewsService.formatDate(news.date)}
          </div>

          {/* Görsel */}
          {allImages.length > 0 && (
            <div className="relative mb-6 rounded-lg overflow-hidden bg-slate-100">
              <img
                key={allImages[imgIndex]}
                src={allImages[imgIndex]}
                alt={`${news.title} - görsel ${imgIndex + 1}`}
                className="w-full h-48 md:h-96 object-cover"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />

              {/* Oklar */}
              {allImages.length > 1 && (
                <>
                  <button
                    onClick={() =>
                      setImgIndex(
                        (i) => (i - 1 + allImages.length) % allImages.length
                      )
                    }
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white w-8 h-8 rounded-full flex items-center justify-center transition"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={() =>
                      setImgIndex((i) => (i + 1) % allImages.length)
                    }
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white w-8 h-8 rounded-full flex items-center justify-center transition"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>

                  {/* Noktalar */}
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {allImages.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setImgIndex(i)}
                        className={`rounded-full transition-all ${
                          i === imgIndex
                            ? "bg-white w-4 h-1.5"
                            : "bg-white/50 w-1.5 h-1.5"
                        }`}
                      />
                    ))}
                  </div>

                  {/* Sayaç */}
                  <span className="absolute top-2 right-2 text-xs bg-black/40 text-white px-2 py-0.5 rounded-full">
                    {imgIndex + 1} / {allImages.length}
                  </span>
                </>
              )}
            </div>
          )}

          {/* İçerik */}
          <div
            className="prose prose-sm md:prose-base max-w-none text-gray-700 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: news.content }}
          />
        </div>

        {/* Footer */}
        <div className="p-4 md:p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full md:w-auto bg-slate-700 hover:bg-slate-800 text-white px-6 py-3 rounded-lg font-semibold transition"
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
};

// Haberler Bölümü
const NewsSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [news, setNews] = useState([]);
  const [selectedNews, setSelectedNews] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    const unsub = NewsService.subscribe((all) => {
      setNews(all.slice(0, 5));
    });
    return () => unsub();
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % news.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + news.length) % news.length);
  };

  const handleShowMore = () => {
    setSelectedNews(news[currentSlide]);
    setIsDialogOpen(true);
  };

  if (news.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 md:p-8 mb-4 md:mb-6">
        <p className="text-center text-gray-500 text-sm md:text-base">
          Haberler yükleniyor...
        </p>
      </div>
    );
  }

  const currentNews = news[currentSlide];

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-4 md:mb-6">
        {/* Video/Görsel Alanı */}
        <div className="relative bg-slate-800 h-48 sm:h-60 md:h-72 overflow-hidden">
          {/* Arka plan görseli */}
          {currentNews.imageUrl && (
            <img
              key={currentNews.imageUrl}
              src={currentNews.imageUrl}
              alt={currentNews.title}
              className="absolute inset-0 w-full h-full object-cover opacity-90"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          )}

          {/* Play Butonu */}
          <div className="absolute inset-0 flex items-center justify-center">
            <button
              onClick={handleShowMore}
              className="bg-white/10 backdrop-blur p-3 md:p-5 rounded-full hover:bg-white/20 transition group"
            >
              <svg
                className="w-8 h-8 md:w-10 md:h-10 text-white"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            </button>
          </div>

          {/* Sol/Sağ Oklar */}
          <button
            onClick={prevSlide}
            className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white p-1.5 md:p-2 rounded transition"
          >
            <svg
              className="w-4 h-4 md:w-5 md:h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          <button
            onClick={nextSlide}
            className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white p-1.5 md:p-2 rounded transition"
          >
            <svg
              className="w-4 h-4 md:w-5 md:h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>

          {/* Haber Başlığı */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3 md:p-4">
            <div className="bg-amber-400 inline-flex items-center px-2 md:px-3 py-1 rounded text-xs md:text-sm font-bold text-gray-800 mb-1 md:mb-2">
              <svg
                className="w-3 h-3 md:w-4 md:h-4 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {NewsService.formatDate(currentNews.date)}
            </div>
            <h3 className="text-white text-sm md:text-xl font-bold line-clamp-2">
              {currentNews.title}
            </h3>
          </div>

          {/* Slider Noktaları */}
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex space-x-1">
            {news.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`h-1.5 md:h-2 rounded-full transition-all ${
                  index === currentSlide
                    ? "bg-amber-400 w-4 md:w-6"
                    : "bg-white/50 w-1.5 md:w-2"
                }`}
              />
            ))}
          </div>
        </div>

        {/* Alt Bilgi ve Buton */}
        <div className="p-3 md:p-4 bg-gray-50 flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <p className="text-xs md:text-sm text-gray-600 flex-1 line-clamp-2">
            {currentNews.excerpt}
          </p>
          <button
            onClick={handleShowMore}
            className="w-full sm:w-auto bg-slate-700 hover:bg-slate-800 text-white px-4 py-2 rounded text-xs md:text-sm font-semibold transition whitespace-nowrap"
          >
            DEVAMINI GÖR
          </button>
        </div>
      </div>

      {/* Haber Detay Dialog */}
      <NewsDialog
        news={selectedNews}
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
      />
    </>
  );
};

// Gold Üye Firmalar Bölümü
const GoldMembersSection = () => {
  const [members, setMembers] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const q = query(
      collection(db, "firmalar"),
      where("isGold", "==", true),
      orderBy("createdAt", "desc")
    );
    const unsub = onSnapshot(q, (snap) => {
      setMembers(
        snap.docs.map((doc) => {
          const d = doc.data();
          return {
            id: doc.id,
            name: d.firmaAdi || "",
            contact: d.yetkiliKisi || "",
            phone: d.telefon || d.gsm || "",
            logoUrl: d.logoUrl || "",
            sektor: d.secilenSektorler?.[0] || "",
            sehir: d.sehir || "",
          };
        })
      );
    });
    return () => unsub();
  }, []);

  return (
    <div className="bg-white rounded-lg border-2 border-amber-400 p-3 md:p-4 mt-4 md:mt-6">
      {/* Başlık */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base md:text-lg font-bold text-gray-800 flex items-center gap-2">
          <span className="w-7 h-7 rounded-lg bg-white/30 flex items-center justify-center">
            <svg
              className="w-4 h-4 text-gray-800"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
              />
            </svg>
          </span>
          GOLD ÜYE FİRMALAR
        </h2>
        <button className="bg-slate-700 hover:bg-slate-800 text-white px-3 md:px-4 py-2 rounded text-xs md:text-sm font-medium transition flex items-center gap-1">
          TÜMÜNÜ GÖR
          <svg
            className="w-3.5 h-3.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>

      {/* 3'lü Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {members.map((member) => (
          <div
            key={member.id}
            onClick={() => navigate(`/firms/${member.id}`)}
            className="group bg-white rounded-xl border border-amber-200 p-4 flex items-center gap-4
              hover:border-amber-400 hover:shadow-lg hover:-translate-y-0.5
              transition-all duration-200 cursor-pointer overflow-hidden relative"
          >
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-amber-400 to-amber-200 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

            <div className="w-14 h-14 rounded-xl bg-amber-50 border border-amber-100 overflow-hidden shrink-0 flex items-center justify-center">
              {member.logoUrl ? (
                <img
                  src={member.logoUrl}
                  alt={member.name}
                  className="w-full h-full object-contain p-1"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              ) : (
                <span className="text-sm font-bold text-amber-600">
                  {(member.name || "?")
                    .split(/\s+/)
                    .slice(0, 2)
                    .map((w) => w[0]?.toLocaleUpperCase("tr") || "")
                    .join("")}
                </span>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 mb-0.5">
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-semibold shrink-0">
                  ⭐ Gold
                </span>
              </div>
              <h3 className="font-semibold text-sm text-slate-800 truncate group-hover:text-amber-700 transition-colors">
                {member.name}
              </h3>
              <div className="flex items-center gap-3 mt-1.5">
                {member.contact && (
                  <span className="text-[11px] text-slate-400 flex items-center gap-1 truncate">
                    <svg
                      className="w-3 h-3 shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                    {member.contact}
                  </span>
                )}
                {member.phone && (
                  <span className="text-[11px] text-slate-400 flex items-center gap-1 shrink-0">
                    <svg
                      className="w-3 h-3 shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                      />
                    </svg>
                    {member.phone}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CategoriesAndNewsSection;
